import { useState, useRef, useCallback } from 'react';

// ── Inline pure-JS CSV parser (handles quoted fields) ─────────────────────────
function parseCSV(text) {
  const lines = [];
  let cur = '';
  let inQuote = false;
  const chars = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  for (let i = 0; i < chars.length; i++) {
    const ch = chars[i];
    if (ch === '"') {
      if (inQuote && chars[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuote = !inQuote;
      }
    } else if (ch === ',' && !inQuote) {
      lines[lines.length - 1] = lines[lines.length - 1] ?? [];
      lines[lines.length - 1].push(cur);
      cur = '';
    } else if (ch === '\n' && !inQuote) {
      if (lines.length === 0) lines.push([]);
      lines[lines.length - 1].push(cur);
      cur = '';
      lines.push([]);
    } else {
      cur += ch;
    }
  }
  // push last field
  if (cur.length > 0 || (lines.length > 0 && lines[lines.length - 1].length > 0)) {
    if (lines.length === 0) lines.push([]);
    lines[lines.length - 1].push(cur);
  }
  // remove trailing empty row
  if (lines.length > 0 && lines[lines.length - 1].length === 1 && lines[lines.length - 1][0] === '') {
    lines.pop();
  }
  return lines;
}

// ── Column definitions per entityType ─────────────────────────────────────────
const ENTITY_FIELDS = {
  customers: [
    { key: 'name',    label: 'Name',    required: true },
    { key: 'email',   label: 'Email',   required: false },
    { key: 'phone',   label: 'Phone',   required: false },
    { key: 'address', label: 'Address', required: false },
    { key: 'notes',   label: 'Notes',   required: false },
  ],
  inventory: [
    { key: 'name',        label: 'Name',         required: true },
    { key: 'brand',       label: 'Brand',        required: false },
    { key: 'sku',         label: 'SKU',          required: false },
    { key: 'qty',         label: 'Qty',          required: false },
    { key: 'costPerUnit', label: 'Cost/Unit',    required: false },
    { key: 'unitType',    label: 'Unit Type',    required: false },
    { key: 'color',       label: 'Color',        required: false },
  ],
  leads: [
    { key: 'name',   label: 'Name',   required: true },
    { key: 'email',  label: 'Email',  required: false },
    { key: 'phone',  label: 'Phone',  required: false },
    { key: 'source', label: 'Source', required: false },
    { key: 'status', label: 'Status', required: false },
    { key: 'notes',  label: 'Notes',  required: false },
  ],
};

const ENTITY_LABELS = { customers: 'Customers', inventory: 'Inventory', leads: 'Leads' };

export default function CSVImportModal({ open, onClose, entityType = 'customers', onImport }) {
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);      // all data rows as arrays
  const [mapping, setMapping] = useState({});       // fieldKey → csvHeader
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const fields = ENTITY_FIELDS[entityType] || ENTITY_FIELDS.customers;
  const requiredFields = fields.filter(f => f.required).map(f => f.key);

  const reset = useCallback(() => {
    setCsvHeaders([]);
    setCsvRows([]);
    setMapping({});
    setError('');
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  const processFile = (file) => {
    if (!file || !file.name.endsWith('.csv')) {
      setError('Please upload a valid .csv file.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const parsed = parseCSV(text);
        if (parsed.length < 2) {
          setError('CSV must have at least a header row and one data row.');
          return;
        }
        const headers = parsed[0];
        const rows = parsed.slice(1);
        setCsvHeaders(headers);
        setCsvRows(rows);
        setError('');

        // Auto-map: find CSV headers that fuzzy-match field labels
        const autoMap = {};
        fields.forEach(({ key, label }) => {
          const match = headers.find(h =>
            h.toLowerCase().trim() === label.toLowerCase() ||
            h.toLowerCase().trim() === key.toLowerCase()
          );
          if (match) autoMap[key] = match;
        });
        setMapping(autoMap);
      } catch {
        setError('Failed to parse CSV. Please check the file format.');
      }
    };
    reader.readAsText(file);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) processFile(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleImport = () => {
    // Map rows to objects using the column mapping
    const mappedRows = csvRows.map(row => {
      const obj = {};
      fields.forEach(({ key }) => {
        const csvHeader = mapping[key];
        if (csvHeader) {
          const colIdx = csvHeaders.indexOf(csvHeader);
          obj[key] = colIdx >= 0 ? (row[colIdx] || '').trim() : '';
        }
      });
      return obj;
    });
    onImport(mappedRows);
    handleClose();
  };

  const allRequiredMapped = requiredFields.every(k => mapping[k]);
  const previewRows = csvRows.slice(0, 5);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden bg-white dark:bg-[#1B2A3E] border border-gray-200 dark:border-[#243348]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-200 dark:border-[#243348]">
          <h2 className="text-[var(--wm-text-body)] font-bold text-[#0F1923] dark:text-[#F8FAFE]">
            Import {ENTITY_LABELS[entityType]} from CSV
          </h2>
          <button
            onClick={handleClose}
            className="w-7 h-7 flex items-center justify-center rounded-md text-[#64748B] hover:bg-gray-100 dark:hover:bg-[#243348] transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">

          {/* Drop zone */}
          {csvHeaders.length === 0 && (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleFileDrop}
              onClick={() => fileRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                dragging
                  ? 'border-[#2E8BF0] bg-[#2E8BF0]/5'
                  : 'border-gray-200 dark:border-[#243348] hover:border-[#2E8BF0]/50'
              }`}
            >
              <svg className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-[#243348]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6h.1a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
              </svg>
              <p className="text-[var(--wm-text-body)] font-semibold text-[#0F1923] dark:text-[#F8FAFE] mb-1">
                Drag and drop a CSV file here
              </p>
              <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">or click to browse</p>
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          )}

          {error && (
            <p className="text-[var(--wm-text-body)] text-red-500 font-medium">{error}</p>
          )}

          {/* Column mapper */}
          {csvHeaders.length > 0 && (
            <>
              <div>
                <p className="text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] mb-2 uppercase tracking-wider">Map Columns</p>
                <div className="grid grid-cols-2 gap-2">
                  {fields.map(({ key, label, required }) => (
                    <div key={key}>
                      <label className="block text-[var(--wm-text-ui)] font-medium text-[#0F1923] dark:text-[#F8FAFE] mb-1">
                        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
                      </label>
                      <select
                        value={mapping[key] || ''}
                        onChange={e => setMapping(prev => ({ ...prev, [key]: e.target.value || undefined }))}
                        className="w-full px-2.5 py-1.5 rounded-lg text-[var(--wm-text-ui)] bg-[#F8FAFE] dark:bg-[#0F1923]/60 border border-gray-200 dark:border-[#243348] text-[#0F1923] dark:text-[#F8FAFE] focus:outline-none focus:ring-2 focus:ring-[#2E8BF0]/30"
                      >
                        <option value="">-- skip --</option>
                        {csvHeaders.map(h => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div>
                <p className="text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] mb-2 uppercase tracking-wider">
                  Preview (first {previewRows.length} of {csvRows.length} rows)
                </p>
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-[#243348]">
                  <table className="w-full text-[var(--wm-text-ui)]">
                    <thead className="bg-gray-50 dark:bg-[#243348]">
                      <tr>
                        {csvHeaders.map(h => (
                          <th key={h} className="px-2.5 py-1.5 text-left text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-[#243348]">
                      {previewRows.map((row, ri) => (
                        <tr key={ri}>
                          {csvHeaders.map((h, ci) => (
                            <td key={h} className="px-2.5 py-1.5 text-[#0F1923] dark:text-[#F8FAFE] truncate max-w-[120px]">
                              {row[ci] || ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Re-upload */}
              <button
                onClick={reset}
                className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] hover:text-[#2E8BF0] dark:hover:text-[#60ABFF] underline transition-colors"
              >
                Upload a different file
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        {csvHeaders.length > 0 && (
          <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-[#243348]">
            <button
              onClick={handleClose}
              className="px-4 py-2 rounded-lg text-[var(--wm-text-body)] font-medium bg-gray-100 dark:bg-[#243348] text-[#0F1923] dark:text-[#F8FAFE] hover:bg-gray-200 dark:hover:bg-[#2A3D55] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={!allRequiredMapped}
              className={`px-4 py-2 rounded-lg text-[var(--wm-text-body)] font-semibold transition-colors ${
                allRequiredMapped
                  ? 'bg-[#2E8BF0] text-white hover:bg-[#2578D4]'
                  : 'bg-gray-200 dark:bg-[#243348] text-[#64748B] cursor-not-allowed'
              }`}
            >
              Import {csvRows.length} row{csvRows.length !== 1 ? 's' : ''}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
